import { useReducer, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import useSWR from 'swr';
import type { Vendor, Position, Category, PositionNames } from '../types';
import { fetchFreshData } from '../data';
import { useCategoryStyleSheet } from '../hooks/useCategoryStyleSheet';
import {
  isAmenityType,
  synthesizeAmenityVendor,
  syntheticCategoryFor,
  type AmenityType,
} from '../utils/amenities';
import {
  MarketDataContext,
  type MarketDataContextValue,
  type MarketDataState,
} from './MarketDataContext.utils';

// Re-export so existing consumers can import the error kind from this module.
export type { MarketDataErrorKind } from './MarketDataContext.utils';

export const VENDORS_SWR_KEY = '/api/vendors';

// Tracks positions that have been locally mutated during an edit session so
// that background SWR revalidations (SET_DATA) don't clobber in-memory edits.
// This is purely internal to the reducer — not exposed via MarketDataContextValue.
//
// Note: EditorModeContext tracks an overlapping set (dirtyPositionUids) for the
// CSV export warning surface. If a future "save to Sheets" flow is added, both
// sets will need to be cleared together at save time.
type LocalEdits = {
  edited: Map<string, Position>;  // uid → full local Position snapshot
  deleted: Set<string>;           // uids of locally-deleted positions
};

type State = MarketDataState & { _local: LocalEdits };

type Action =
  | { type: 'SET_DATA'; vendors: Vendor[]; positions: Position[]; categories: Category[]; names: PositionNames; positionWarnings: string[] }
  | { type: 'MOVE_POSITION'; uid: string; coordinates: [number, number] }
  | { type: 'MOVE_POSITIONS_BULK'; updates: ReadonlyMap<string, [number, number]> }
  | { type: 'ADD_POSITION'; position: Position }
  | { type: 'UPDATE_POSITION'; uid: string; patch: Partial<Pick<Position, 'type' | 'labelPosition' | 'name' | 'description'>> }
  | { type: 'RENAME_POSITION'; uid: string; newId: string }
  | { type: 'DELETE_POSITION'; uid: string }
  | { type: 'SET_FILTER'; search: string }
  | { type: 'SET_CATEGORY_FILTER'; categoryIds: Set<string> }
  | { type: 'SHOW_ALL_CATEGORIES' };

const initialState: State = {
  vendors: [],
  positions: [],
  categories: [],
  names: {},
  filters: { search: '', categoryIds: new Set() },
  positionWarnings: [],
  _local: { edited: new Map(), deleted: new Set() },
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_DATA': {
      const isInitial = state.vendors.length === 0 && state.categories.length === 0;
      let categoryIds: Set<string>;
      if (isInitial) {
        categoryIds = new Set(action.categories.map(c => c.id));
      } else {
        // Keep "all on" if the user hadn't narrowed to a subset. This handles
        // SWR refreshes where categories are added or removed: if the user was
        // showing everything, they should keep showing everything.
        const wasAllOn = state.categories.every(c => state.filters.categoryIds.has(c.id));
        if (wasAllOn) {
          categoryIds = new Set(action.categories.map(c => c.id));
        } else {
          // User had a specific subset selected: preserve it, dropping any IDs
          // that no longer exist in the fresh categories.
          const freshCatIds = new Set(action.categories.map(c => c.id));
          categoryIds = new Set([...state.filters.categoryIds].filter(id => freshCatIds.has(id)));
          // If every previously-selected category was removed by the refresh,
          // fall back to all-on: "show nothing" is rarely intentional and is
          // more likely a stuck state caused by category renaming/cleanup.
          if (categoryIds.size === 0) categoryIds = new Set(action.categories.map(c => c.id));
        }
      }

      const { edited, deleted } = state._local;
      const hasLocalEdits = edited.size > 0 || deleted.size > 0;

      if (!hasLocalEdits) {
        // Fast path: no in-progress editor session — replace wholesale.
        return {
          ...state,
          vendors: action.vendors,
          positions: action.positions,
          categories: action.categories,
          names: action.names,
          positionWarnings: action.positionWarnings,
          filters: { ...state.filters, categoryIds },
        };
      }

      // Slow path: merge server data while preserving local edits.
      //
      // Positions: for each server position...
      //   • skip if locally deleted
      //   • use the local snapshot if dirty (preserves moved coords + field edits)
      //   • otherwise take the server version verbatim
      // Then append locally-added positions (not yet on the server).
      const serverPositionUids = new Set(action.positions.map(p => p.uid));
      const mergedPositions: Position[] = action.positions
        .filter(p => !deleted.has(p.uid))
        .map(p => edited.get(p.uid) ?? p);
      for (const [uid, pos] of edited) {
        if (!serverPositionUids.has(uid)) mergedPositions.push(pos);
      }

      // Vendors: patch coordinates for positions that were locally moved.
      // Vendor metadata (name, description, etc.) still updates from the server.
      // Also exclude vendors whose positions were locally deleted, and preserve
      // synthetic vendors for locally-added positions.
      const serverVendorPositionUids = new Set(action.vendors.map(v => v.positionUid));
      const mergedVendors: Vendor[] = action.vendors
        .filter(v => !deleted.has(v.positionUid))
        .map(v => {
          const editedPos = edited.get(v.positionUid);
          return editedPos ? { ...v, coordinates: editedPos.coordinates } : v;
        });
      for (const [uid, pos] of edited) {
        if (!serverVendorPositionUids.has(uid)) {
          // Locally-added position — keep all its vendors from current state.
          // Use filter (not find) so multi-vendor synthetic positions (if they
          // ever exist) all survive the merge rather than only the first match.
          for (const v of state.vendors.filter(sv => sv.positionUid === uid)) {
            mergedVendors.push({ ...v, coordinates: pos.coordinates });
          }
        }
      }

      return {
        ...state,
        vendors: mergedVendors,
        positions: mergedPositions,
        categories: action.categories,
        names: action.names,
        positionWarnings: action.positionWarnings,
        filters: { ...state.filters, categoryIds },
        // _local is preserved: edits remain valid until page reload or explicit reset.
      };
    }
    case 'MOVE_POSITION': {
      const { uid, coordinates } = action;
      const updatedPositions = state.positions.map(p =>
        p.uid === uid ? { ...p, coordinates } : p
      );
      const movedPos = updatedPositions.find(p => p.uid === uid);
      const edited = new Map(state._local.edited);
      if (movedPos) edited.set(uid, movedPos);
      return {
        ...state,
        positions: updatedPositions,
        vendors: state.vendors.map(v =>
          v.positionUid === uid ? { ...v, coordinates } : v
        ),
        _local: { ...state._local, edited },
      };
    }
    case 'MOVE_POSITIONS_BULK': {
      const { updates } = action;
      if (updates.size === 0) return state;
      const updatedPositions = state.positions.map(p => {
        const next = updates.get(p.uid);
        return next ? { ...p, coordinates: next } : p;
      });
      const edited = new Map(state._local.edited);
      for (const uid of updates.keys()) {
        const movedPos = updatedPositions.find(p => p.uid === uid);
        if (movedPos) edited.set(uid, movedPos);
      }
      return {
        ...state,
        positions: updatedPositions,
        vendors: state.vendors.map(v => {
          const next = updates.get(v.positionUid);
          return next ? { ...v, coordinates: next } : v;
        }),
        _local: { ...state._local, edited },
      };
    }
    case 'ADD_POSITION': {
      const synth = synthesizeAmenityVendor(action.position);
      const edited = new Map(state._local.edited);
      edited.set(action.position.uid, action.position);
      if (!synth) {
        return {
          ...state,
          positions: [...state.positions, action.position],
          _local: { ...state._local, edited },
        };
      }
      // New amenity/stage position: synthesize a vendor and ensure the
      // category + filter chip are present so the marker is immediately visible.
      // Editor intent: "place and see the marker." Override user filter state
      // because the alternative is a silently-invisible marker with no feedback.
      const synthCat = syntheticCategoryFor(action.position.type as AmenityType);
      const categoryAlreadyExists = state.categories.some(c => c.id === synthCat.id);
      const categoryVisible = state.filters.categoryIds.has(synthCat.id);
      return {
        ...state,
        positions: [...state.positions, action.position],
        vendors: [...state.vendors, synth],
        categories: categoryAlreadyExists ? state.categories : [...state.categories, synthCat],
        filters: categoryVisible
          ? state.filters
          : { ...state.filters, categoryIds: new Set([...state.filters.categoryIds, synthCat.id]) },
        _local: { ...state._local, edited },
      };
    }
    case 'UPDATE_POSITION': {
      const pos = state.positions.find(p => p.uid === action.uid);
      if (!pos) return state;
      const updatedPos: Position = { ...pos, ...action.patch };
      const oldIsAmenity = isAmenityType(pos.type);
      const newIsAmenity = isAmenityType(updatedPos.type);

      const updatedPositions = state.positions.map(p =>
        p.uid === action.uid ? updatedPos : p
      );

      let updatedVendors: Vendor[];
      if (oldIsAmenity && newIsAmenity) {
        // amenity → amenity: re-synthesize to pick up type / name / description changes.
        // Filter state is deliberately left alone — marker is already visible.
        const synth = synthesizeAmenityVendor(updatedPos);
        if (!synth) return state;
        updatedVendors = state.vendors.map(v => v.positionUid === action.uid ? synth : v);
      } else if (!oldIsAmenity && newIsAmenity) {
        // booth → amenity: replace any bound real vendor with a synthetic one
        const synth = synthesizeAmenityVendor(updatedPos);
        if (!synth) return state;
        updatedVendors = [...state.vendors.filter(v => v.positionUid !== action.uid), synth];
      } else if (oldIsAmenity && !newIsAmenity) {
        // amenity → booth: drop the synthetic vendor. This position now needs a real
        // Vendors-sheet row to render in normal mode. CoordinateExport surfaces a
        // window.alert to editor staff when this transition is made via the form.
        // Filter state is deliberately left alone — no amenity marker remains.
        updatedVendors = state.vendors.filter(v => v.positionUid !== action.uid);
      } else {
        // booth → booth: patch type and labelPosition on the bound real vendor
        updatedVendors = state.vendors.map(v => {
          if (v.positionUid !== action.uid) return v;
          const next = { ...v };
          if (action.patch.type !== undefined) next.type = action.patch.type;
          if (action.patch.labelPosition !== undefined) next.labelPosition = action.patch.labelPosition;
          return next;
        });
      }

      // Ensure the right synthetic category and filter are enabled for amenity types.
      // booth → amenity: always enable the new category's filter.
      // amenity → amenity (same category): leave filter alone — marker was already visible.
      // amenity → amenity (cross-category, e.g. restroom→stage): also enable the new
      //   filter, because the old category's filter being on doesn't cover the new one.
      const editedUpdate = new Map(state._local.edited);
      editedUpdate.set(action.uid, updatedPos);
      let updatedCategories = state.categories;
      let updatedFilters = state.filters;
      if (newIsAmenity) {
        const synthCat = syntheticCategoryFor(updatedPos.type as AmenityType);
        const oldSynthCat = oldIsAmenity ? syntheticCategoryFor(pos.type as AmenityType) : null;
        if (!updatedCategories.some(c => c.id === synthCat.id)) {
          updatedCategories = [...updatedCategories, synthCat];
        }
        if (!updatedFilters.categoryIds.has(synthCat.id) &&
            (oldSynthCat === null || oldSynthCat.id !== synthCat.id)) {
          updatedFilters = { ...updatedFilters, categoryIds: new Set([...updatedFilters.categoryIds, synthCat.id]) };
        }
      }
      return {
        ...state,
        positions: updatedPositions,
        vendors: updatedVendors,
        categories: updatedCategories,
        filters: updatedFilters,
        _local: { ...state._local, edited: editedUpdate },
      };
    }
    case 'RENAME_POSITION': {
      const { uid, newId } = action;
      const renamedPositions = state.positions.map(p =>
        p.uid === uid ? { ...p, positionId: newId } : p
      );
      const renamedPos = renamedPositions.find(p => p.uid === uid);
      const editedRename = new Map(state._local.edited);
      if (renamedPos) editedRename.set(uid, renamedPos);
      return {
        ...state,
        positions: renamedPositions,
        vendors: state.vendors.map(v =>
          v.positionUid === uid ? { ...v, positionId: newId } : v
        ),
        _local: { ...state._local, edited: editedRename },
      };
    }
    case 'DELETE_POSITION': {
      const editedDel = new Map(state._local.edited);
      editedDel.delete(action.uid);
      const deletedDel = new Set(state._local.deleted);
      deletedDel.add(action.uid);
      return {
        ...state,
        positions: state.positions.filter(p => p.uid !== action.uid),
        vendors: state.vendors.filter(v => v.positionUid !== action.uid),
        _local: { edited: editedDel, deleted: deletedDel },
      };
    }
    case 'SET_FILTER':
      return { ...state, filters: { ...state.filters, search: action.search } };
    case 'SET_CATEGORY_FILTER':
      return { ...state, filters: { ...state.filters, categoryIds: action.categoryIds } };
    case 'SHOW_ALL_CATEGORIES':
      return {
        ...state,
        filters: {
          ...state.filters,
          categoryIds: new Set(state.categories.map(c => c.id)),
        },
      };
    default:
      return state;
  }
}

export function MarketDataProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useCategoryStyleSheet(state.categories);

  // Disable focus/reconnect revalidation to match the old code's behavior:
  // only fetch on mount and on explicit refresh(). Without this, a tab-out/
  // tab-in would trigger SET_DATA and overwrite any unsaved editor drags.
  const { data } = useSWR(VENDORS_SWR_KEY, fetchFreshData, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  useEffect(() => {
    if (data) {
      dispatch({
        type: 'SET_DATA',
        vendors: data.vendors,
        positions: data.positions,
        categories: data.categories,
        names: data.names,
        positionWarnings: data.positionWarnings,
      });
    }
  }, [data]);

  const setSearch = useCallback((search: string) => {
    dispatch({ type: 'SET_FILTER', search });
  }, []);
  // SHOW_ALL_CATEGORIES reads the current categories list inside the
  // reducer (no need for a stale-closure ref dance).
  const showAllCategories = useCallback(() => {
    dispatch({ type: 'SHOW_ALL_CATEGORIES' });
  }, []);
  const setCategoryFilter = useCallback((ids: Set<string>) => {
    dispatch({ type: 'SET_CATEGORY_FILTER', categoryIds: ids });
  }, []);
  const movePosition = useCallback((uid: string, coordinates: [number, number]) => {
    dispatch({ type: 'MOVE_POSITION', uid, coordinates });
  }, []);
  const movePositionsBulk = useCallback(
    (updates: ReadonlyMap<string, [number, number]>) => {
      dispatch({ type: 'MOVE_POSITIONS_BULK', updates });
    },
    [],
  );
  const addPosition = useCallback((position: Position) => {
    dispatch({ type: 'ADD_POSITION', position });
  }, []);
  const updatePosition = useCallback(
    (uid: string, patch: Partial<Pick<Position, 'type' | 'labelPosition' | 'name' | 'description'>>) => {
      dispatch({ type: 'UPDATE_POSITION', uid, patch });
    },
    [],
  );
  const renamePosition = useCallback((uid: string, newId: string) => {
    dispatch({ type: 'RENAME_POSITION', uid, newId });
  }, []);
  const deletePosition = useCallback((uid: string) => {
    dispatch({ type: 'DELETE_POSITION', uid });
  }, []);

  const value = useMemo<MarketDataContextValue>(
    () => ({
      state,
      deletedPositionUids: state._local.deleted,
      setSearch,
      showAllCategories,
      setCategoryFilter,
      movePosition,
      movePositionsBulk,
      addPosition,
      updatePosition,
      renamePosition,
      deletePosition,
    }),
    [
      state,
      setSearch,
      showAllCategories,
      setCategoryFilter,
      movePosition,
      movePositionsBulk,
      addPosition,
      updatePosition,
      renamePosition,
      deletePosition,
    ],
  );

  return (
    <MarketDataContext.Provider value={value}>
      {children}
    </MarketDataContext.Provider>
  );
}
